// @ts-strict-ignore
import { CardCollectionAgendaTemplate } from '@coast/schemas';
import { JSONSchema7 } from 'json-schema';
import { findLast, isEmpty, sortBy, sum } from 'lodash';
import moment from 'moment';
import { AGENDA_LOADING_HEIGHT } from '@shared/scenes/cards/agenda/components/item';
import { AGENDA_EMPTY_DAY_HEIGHT } from '@shared/scenes/cards/agenda/components/item/empty';
import { AGENDA_MONTH_HEADER_HEIGHT } from '@shared/scenes/cards/agenda/components/item/month';
import { AGENDA_WEEK_HEADER_HEIGHT } from '@shared/scenes/cards/agenda/components/item/week';
import { selectAgendaCard } from '@shared/scenes/cards/util';

interface BaseAgendaItem {
  key: string;
  height?: number;
  offset?: number;
}

export interface AgendaDayItem extends BaseAgendaItem {
  type: 'day';
  dateString: DateString;
  cards: MBAPI.Card[];
  isToday?: boolean;
}

export interface AgendaWeekHeaderItem extends BaseAgendaItem {
  type: 'week';
  label: string;
  dateString: DateString;
}

export interface AgendaMonthHeaderItem extends BaseAgendaItem {
  type: 'month';
  label: string;
  dateString: DateString;
}

export interface AgendaLoadingItem extends BaseAgendaItem {
  type: 'loading';
}

export type AgendaDateItem = AgendaDayItem | AgendaWeekHeaderItem | AgendaMonthHeaderItem;
export type AgendaItem =
  | AgendaDayItem
  | AgendaWeekHeaderItem
  | AgendaMonthHeaderItem
  | AgendaLoadingItem;

export type CardsByDate = { [key: string]: MBAPI.Card[] };

export type ISOString = string;
/**
 * String matching format YYYY-MM-DD
 */
export type DateString = string;
/**
 * String matching format YYYY-MM-01
 */
export type MonthString = string;

export type DateRange = [DateString, DateString];

export const MONTH_FORMAT = 'MMMM YYYY';
export const DATE_FORMAT = 'YYYY-MM-DD';

export function getShortDayNames(): string[] {
  let names: string[] = [];
  const m = moment().startOf('week');
  for (let i = 0; i < 7; i++) {
    names.push(m.format('dd')[0]);
    m.add(1, 'day');
  }
  return names;
}

function weekOf(ds: DateString): string {
  const s = moment(ds);
  const e = moment(ds).add(6, 'days');
  if (s.month() === e.month()) {
    return `${s.format('MMMM D')} - ${e.format('D')}`;
  }
  return `${s.format('MMMM D')} - ${e.format('MMMM D')}`;
}

export function getCardsHeight(cards: MBAPI.Card[]): number {
  let height = 0;
  cards.forEach(card => {
    height += 24; // magic number: top & bottom card padding + top margin
    !!card.fields.name && (height += 28); // magic number: size name block
    (!!card.startDate || !!card.endDate) && (height += 28); // magic number: size of date range block
    card.fields['assignees']?.length > 0 && (height += 30); // magic number: size of assignee block
    card.fields['assignees']?.length === 0 && (height += 28); // magic number: size of assignee block (empty)
    !card.fields['assignees'] && (height += 28); // magic number: size of assignee block (empty)
  });
  return height;
}

function createItem(
  item: AgendaItem,
  offset: number,
  calculateCardHeight: (card: MBAPI.Card) => number,
): AgendaItem {
  let height;
  if (item.type === 'week') {
    height = AGENDA_WEEK_HEADER_HEIGHT;
  } else if (item.type === 'month') {
    height = AGENDA_MONTH_HEADER_HEIGHT;
  } else if (item.type === 'loading') {
    height = AGENDA_LOADING_HEIGHT;
  } else if (item.type === 'day' && item.cards.length === 0) {
    height = AGENDA_EMPTY_DAY_HEIGHT;
  } else {
    height = AGENDA_EMPTY_DAY_HEIGHT + sum(item.cards.map(calculateCardHeight));
  }
  item.offset = offset;
  item.height = height;
  return item;
}

export function getContentHeight(data: AgendaItem[]) {
  return data[data.length - 1].offset + data[data.length - 1].height;
}

export function getCardsByDate(
  cards: MBAPI.Card[],
  definition: JSONSchema7,
  template: CardCollectionAgendaTemplate,
): CardsByDate {
  let d: CardsByDate = {};
  cards
    .filter(card => selectAgendaCard(card, definition, template))
    .forEach(card => {
      if (card.startDate) {
        const key = dateString(card.startDate);
        if (d[key]) {
          d[key] = sortBy([...d[key], card], 'startDate');
        } else {
          d[key] = [card];
        }
      }
    });
  return d;
}

export function getData(
  cardsByDate: CardsByDate,
  dateRange: DateRange,
  calculateCardHeight: (card: MBAPI.Card) => number,
): AgendaItem[] {
  let data: AgendaItem[] = [];
  let offset = 0;

  if (dateRange[0] === null) {
    return data;
  }

  data.push(createItem({ type: 'loading', key: 'loadPrevious' }, offset, calculateCardHeight));
  offset += data[data.length - 1].height;

  const index = moment(dateRange[0]);

  while (index.isSameOrBefore(dateRange[1])) {
    const key = index.format(DATE_FORMAT);
    if (index.date() === 1) {
      data.push(
        createItem(
          {
            key: key + 'm',
            type: 'month',
            dateString: key,
            label: moment(key).format('MMMM YYYY'),
          },
          offset,
          calculateCardHeight,
        ),
      );
      offset += data[data.length - 1].height;
    } else if (moment(key).day() === 0) {
      data.push(
        createItem(
          {
            key: key + 'w',
            type: 'week',
            dateString: key,
            label: weekOf(key),
          },
          offset,
          calculateCardHeight,
        ),
      );
      offset += data[data.length - 1].height;
    }
    const item: AgendaDayItem = {
      key,
      type: 'day',
      dateString: key,
      cards: cardsByDate[key] || [],
      isToday: index.isSame(moment(), 'day'),
    };
    data.push(createItem(item, offset, calculateCardHeight));

    // increment loop
    index.add(1, 'day');

    // increment offset by height of last-added item
    offset += data[data.length - 1].height;
  }

  data.push(createItem({ type: 'loading', key: 'loadNext' }, offset, calculateCardHeight));

  return data;
}

export function dateString(val?: moment.MomentInput): DateString {
  return moment(val).format(DATE_FORMAT);
}
export function monthString(val?: moment.MomentInput): DateString {
  return moment(val).startOf('month').format(DATE_FORMAT);
}
export function lastMonth(val?: moment.MomentInput): DateString {
  return moment(val).subtract(1, 'month').format(DATE_FORMAT);
}
export function nextMonth(val?: moment.MomentInput): DateString {
  return moment(val).add(1, 'month').format(DATE_FORMAT);
}

export function getRangeFromData(data: AgendaItem[], fallback?: DateRange): DateRange {
  if (isEmpty(data)) {
    return fallback;
  }
  const start = data.find((item: AgendaDateItem) => item?.dateString) as AgendaDateItem;
  const end = findLast(data, (item: AgendaDateItem) => item?.dateString) as AgendaDateItem;
  return [start.dateString, end.dateString];
}

export function isMonthString(ds: DateString): boolean {
  return /(\d{4})-(\d{2})-01/.test(ds);
}

export function getIndexForDate(ds: DateString, data: AgendaItem[]) {
  return data.findIndex(item => item.type === 'day' && item.dateString === ds);
}

export function getInitialRange(today: DateString): DateRange {
  return [dateString(moment(today).subtract(3, 'days')), dateString(moment(today).add(7, 'days'))];
}

export function getItemLayout(
  data: AgendaItem[],
  index: number,
): { length: number; offset: number; index: number } {
  return {
    length: data[index]?.height || 0,
    offset: data[index]?.offset || 0,
    index,
  };
}

export function mergeRanges(r1: DateRange, r2: DateRange): DateRange {
  return [
    moment(r1[0]).isBefore(r2[0]) ? r1[0] : r2[0],
    moment(r1[1]).isAfter(r2[1]) ? r1[1] : r2[1],
  ];
}
